import { Box, Flex, HStack, Tag, TagLabel, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { CustomFieldValue } from '../../../types';

interface ExtractMetadataProps {
  customField?: CustomFieldValue[];
}

const ExtractMetadata: React.FC<ExtractMetadataProps> = ({
  customField,
}) => {
  const navigate = useNavigate();

  // Handle cases where customField is undefined, null, empty array, or has no value
  if (
    !customField ||
    customField.length === 0 ||
    !customField[0] ||
    !customField[0].value ||
    typeof customField[0].value !== 'string' ||
    !customField[0].value.trim()
  ) {
    return (
      <Text
        fontSize="0.875rem"
        fontWeight="700"
        color="text.primary"
        lineHeight={'1.25rem'}
      >
        -
      </Text>
    );
  }

  const field = customField[0];
  const providers =
    field.metadata && Array.isArray(field.metadata.providers)
      ? field.metadata.providers.filter(
          (p): p is { providerId: string; providerLabel: string } =>
            typeof p === 'object' &&
            p !== null &&
            'providerId' in p &&
            'providerLabel' in p
        )
      : [];

  // Support new department-based format, single format, and old array format for backward compatibility
  const resourceProvidersByDepartment = Array.isArray(field.metadata?.resourceProvidersByDepartment)
    ? (field.metadata.resourceProvidersByDepartment as Array<{
        department: string;
        departmentLink?: string;
        practiceId: string;
        resourceProvider: {
          id: string;
          label: string;
          npi?: string;
          specialty?: string;
          tablespace: string;
        };
      }>)
    : [];

  // Fallback: Support single resourceProvider format (backward compatibility)
  const resourceProvider = !resourceProvidersByDepartment.length && field.metadata?.resourceProvider
    ? (field.metadata.resourceProvider as {
        id: string;
        label: string;
        npi?: string;
        specialty?: string;
        tablespace: string;
      })
    : null;

  // Fallback to old array format for backward compatibility
  const resourceProviders =
    !resourceProvidersByDepartment.length &&
    !resourceProvider &&
    field.metadata &&
    Array.isArray(field.metadata.resourceProviders)
      ? field.metadata.resourceProviders.filter(
          (p): p is { id: string; label: string; npi?: string; specialty?: string } =>
            typeof p === 'object' &&
            p !== null &&
            'id' in p &&
            'label' in p
        )
      : [];

  const isNavigable =
    (field.value === 'May cross-schedule with' ||
      field.value === 'Only schedule with') &&
    providers.length > 0;

  const hasResourceProvider =
    field.value === 'Resource Providers' &&
    (resourceProvidersByDepartment.length > 0 || resourceProvider !== null || resourceProviders.length > 0);

  return (
    <Flex direction="column" gap="0.25rem">
      <Text fontSize="0.875rem" fontWeight="700" color="blue.700" lineHeight="1.25rem">
        {field.value}
      </Text>
      {isNavigable && (
        <HStack spacing={1} flexWrap="wrap">
          {providers.map((p) => (
            <Tag
              as="button"
              key={p.providerId}
              size="sm"
              colorScheme="blue"
              borderRadius="full"
              variant="outline"
              cursor="pointer"
              onClick={() => navigate(`/providers/${p.providerId}`)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  navigate(`/providers/${p.providerId}`);
                }
              }}
              _hover={{ bg: 'blue.50', borderColor: 'blue.400', transform: 'translateY(-1px)' }}
              _active={{ transform: 'translateY(0)' }}
              transition="all 0.15s ease-in-out"
              fontSize="0.75rem"
              fontWeight="500"
              tabIndex={0}
            >
              <TagLabel>{p.providerLabel}</TagLabel>
            </Tag>
          ))}
        </HStack>
      )}
      {hasResourceProvider && (
        <Flex direction="column" gap="0.5rem">
          {/* New department-based format */}
          {resourceProvidersByDepartment.length > 0 && (
            <>
              {resourceProvidersByDepartment.map((deptEntry) => (
                <Box key={`${deptEntry.practiceId}-${deptEntry.department}`}>
                  <Text fontSize="xs" fontWeight="600" color="gray.600" mb="0.25rem">
                    {deptEntry.department}:
                  </Text>
                  <HStack spacing={1} flexWrap="wrap" ml="0.5rem">
                    <Tag
                      size="sm"
                      colorScheme="purple"
                      borderRadius="full"
                      variant="outline"
                      fontSize="0.75rem"
                      fontWeight="500"
                    >
                      <TagLabel>
                        {deptEntry.resourceProvider.label}
                        {deptEntry.resourceProvider.tablespace && ` (Tablespace: ${deptEntry.resourceProvider.tablespace})`}
                        {deptEntry.resourceProvider.npi && ` (NPI: ${deptEntry.resourceProvider.npi})`}
                      </TagLabel>
                    </Tag>
                  </HStack>
                </Box>
              ))}
            </>
          )}
          {/* Fallback: Single resourceProvider format (backward compatibility) */}
          {resourceProvidersByDepartment.length === 0 && resourceProvider && (
            <HStack spacing={1} flexWrap="wrap">
              <Tag
                key={resourceProvider.id}
                size="sm"
                colorScheme="purple"
                borderRadius="full"
                variant="outline"
                fontSize="0.75rem"
                fontWeight="500"
              >
                <TagLabel>
                  {resourceProvider.label}
                  {resourceProvider.tablespace && ` (Tablespace: ${resourceProvider.tablespace})`}
                  {resourceProvider.npi && ` (NPI: ${resourceProvider.npi})`}
                </TagLabel>
              </Tag>
            </HStack>
          )}
          {/* Fallback: Old array format (backward compatibility) */}
          {resourceProvidersByDepartment.length === 0 && !resourceProvider && resourceProviders.length > 0 && (
            <HStack spacing={1} flexWrap="wrap">
              {resourceProviders.map((p) => (
                <Tag
                  key={p.id}
                  size="sm"
                  colorScheme="purple"
                  borderRadius="full"
                  variant="outline"
                  fontSize="0.75rem"
                  fontWeight="500"
                >
                  <TagLabel>
                    {p.label}
                    {p.npi && ` (NPI: ${p.npi})`}
                  </TagLabel>
                </Tag>
              ))}
            </HStack>
          )}
        </Flex>
      )}
    </Flex>
  );
};

export default ExtractMetadata;
